import { useEffect } from "react";
import LegalLayout from "@/components/LegalLayout";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

export default function Privacidad() {
    useEffect(() => {
        document.title = "Política de privacidad — Aurum Vanguard";
        window.scrollTo({ top: 0 });
    }, []);

    return (
        <>
            <Navigation />
            <LegalLayout
                title="Política de privacidad"
                subtitle="Cómo recogemos, tratamos y protegemos tus datos personales conforme a los estándares más exigentes de la Unión Europea y de los Emiratos Árabes Unidos."
                lastUpdate="12 de mayo de 2026"
            >
                <h2>1. Responsable del tratamiento</h2>
                <p>
                    El responsable del tratamiento de tus datos personales es <strong>Aurum
                    Vanguard — Strategic Growth & Innovation Firm</strong>, con domicilio
                    profesional en Dubai, Emiratos Árabes Unidos, y dirección de contacto en
                    materia de protección de datos: <a href="mailto:hello@aurumvanguard.com">hello@aurumvanguard.com</a>.
                </p>

                <h2>2. Marcos normativos aplicables</h2>
                <p>El tratamiento se realiza con sometimiento simultáneo a:</p>
                <ul>
                    <li>
                        <strong>Reglamento (UE) 2016/679 (RGPD)</strong> y la Ley Orgánica
                        3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía
                        de los derechos digitales (LOPDGDD) para usuarios residentes en la
                        Unión Europea.
                    </li>
                    <li>
                        <strong>Directiva 2002/58/CE (ePrivacy)</strong> y Ley 34/2002, de
                        Servicios de la Sociedad de la Información y de Comercio Electrónico
                        (LSSI-CE) en materia de cookies y comunicaciones electrónicas.
                    </li>
                    <li>
                        <strong>UAE Federal Decree-Law nº 45/2021</strong> on the Protection of
                        Personal Data, y <strong>DIFC Data Protection Law nº 5 of 2020</strong>
                        cuando el tratamiento se localiza en jurisdicción emiratí.
                    </li>
                    <li>
                        Estándares de seguridad de la información alineados con la familia
                        <strong> ISO/IEC 27001 y 27701</strong>.
                    </li>
                </ul>

                <h2>3. Datos que recogemos</h2>
                <p>Recogemos exclusivamente los datos que tú nos facilitas a través del formulario de contacto:</p>
                <ul>
                    <li>Nombre completo.</li>
                    <li>Email profesional.</li>
                    <li>Empresa o proyecto (opcional).</li>
                    <li>Sector de actividad.</li>
                    <li>Nivel de partnership que te interesa (opcional).</li>
                    <li>Descripción breve de tu situación actual.</li>
                    <li>Preferencia de canal de contacto (email, WhatsApp o teléfono).</li>
                </ul>
                <p>
                    No recogemos categorías especiales de datos. No utilizamos cookies de
                    publicidad ni de tracking de terceros. Los datos técnicos del navegador
                    (dirección IP, user-agent) se procesan transitoriamente con la única
                    finalidad de protección anti-spam y seguridad del servicio (interés
                    legítimo del responsable, art. 6.1.f RGPD).
                </p>

                <h2>4. Finalidades y base jurídica</h2>
                <ul>
                    <li>
                        <strong>Atender tu solicitud de contacto y elaborar una propuesta de
                        colaboración.</strong> Base jurídica: ejecución de medidas
                        precontractuales a petición del interesado (art. 6.1.b RGPD) y
                        consentimiento expreso al marcar la casilla del formulario.
                    </li>
                    <li>
                        <strong>Cumplimiento de obligaciones legales</strong> contables,
                        fiscales y mercantiles cuando proceda (art. 6.1.c RGPD).
                    </li>
                    <li>
                        <strong>Seguridad del servicio</strong> y prevención de abusos
                        automatizados (interés legítimo, art. 6.1.f RGPD).
                    </li>
                </ul>

                <h2>5. Encargados de tratamiento y transferencias internacionales</h2>
                <p>
                    Para prestar este servicio recurrimos a proveedores tecnológicos cualificados
                    que actúan como encargados del tratamiento bajo contrato de encargo
                    conforme al art. 28 RGPD:
                </p>
                <ul>
                    <li>
                        <strong>Resend, Inc.</strong> (Estados Unidos) para la entrega del email
                        de notificación. Transferencia amparada por cláusulas contractuales
                        tipo de la Comisión Europea y por el EU–US Data Privacy Framework.
                    </li>
                    <li>
                        <strong>MongoDB Atlas / proveedor de hosting europeo</strong> para el
                        almacenamiento cifrado del registro de la solicitud.
                    </li>
                    <li>
                        <strong>Cal.com</strong> para la programación de llamadas, si optas por
                        agendar directamente.
                    </li>
                </ul>
                <p>
                    Todos los encargados están sujetos a obligaciones de confidencialidad,
                    cifrado en tránsito (TLS 1.2+) y en reposo, y eliminación al término de la
                    relación contractual.
                </p>

                <h2>6. Plazo de conservación</h2>
                <p>
                    Conservamos los datos durante el tiempo necesario para gestionar la
                    relación comercial y, posteriormente, durante los plazos legalmente
                    exigibles para atender posibles responsabilidades, con un máximo
                    orientativo de <strong>cinco años</strong> desde el último contacto, salvo
                    obligación legal específica de mayor duración o solicitud de supresión por
                    tu parte.
                </p>

                <h2>7. Tus derechos</h2>
                <p>
                    Puedes ejercer en cualquier momento los derechos de acceso, rectificación,
                    supresión, oposición, limitación del tratamiento y portabilidad,
                    escribiendo a <a href="mailto:hello@aurumvanguard.com">hello@aurumvanguard.com</a>
                    con copia de un documento identificativo. Tienes derecho a presentar
                    reclamación ante la <strong>Agencia Española de Protección de Datos</strong>
                    (www.aepd.es) si resides en España, o ante la autoridad de control de tu
                    país de residencia en la UE. En EAU, la autoridad competente es la
                    <strong> UAE Data Office</strong>.
                </p>

                <h2>8. Decisiones automatizadas</h2>
                <p>
                    No se aplican decisiones automatizadas con efectos jurídicos significativos
                    sobre los interesados, ni elaboración de perfiles. La evaluación de tu
                    solicitud la realiza siempre un miembro humano del equipo de Aurum
                    Vanguard.
                </p>

                <h2>9. Seguridad</h2>
                <p>
                    Aplicamos medidas técnicas y organizativas apropiadas alineadas con la
                    familia ISO/IEC 27001: cifrado en tránsito y en reposo, gestión de accesos
                    bajo principio de mínimo privilegio, segregación de entornos, registro de
                    auditoría, política de contraseñas robustas con doble factor para todo el
                    personal, y revisión periódica de vulnerabilidades.
                </p>

                <h2>10. Cambios en esta política</h2>
                <p>
                    Esta política puede actualizarse para reflejar cambios normativos o de
                    nuestros procesos. La versión vigente es siempre la publicada en esta
                    página con su fecha de última actualización.
                </p>
            </LegalLayout>
            <Footer />
        </>
    );
}
